import React, { useEffect, useState } from "react";
import { FormBuilder, Components } from "react-formio";
import { Form as TitleForm, Input, Button, Row, Col, Select, Spin, Space, Radio, Popover, notification } from "antd";
import base64 from "base-64";
import FormService from "../networkservice/FormService";
import "./formcreator.css";
import "../../../../experience/generic/elements/backbutton/backbutton.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../../experience/utils/configurationconstants";
import isEqual from "lodash/isEqual";
import BackButton from "../../../../experience/generic/elements/backbutton/BackButton";
import { executeIntent } from "../../../../experience/utils/IntentExecutor";
import IntentType from "../../../../intentexecutor/utils/IntentType";

import components from "../customformcomponent";
import { useGetOrgIdentifier } from "../../../../experience/generic/hooks/useGetOrgIdentifier";
import { useNavigate } from "react-router";
import { publishEvent } from "../../EventPublisher";
import { useReadMemory } from "../../../../experience/generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { InfoCircleFilled } from "@ant-design/icons";

Components.setComponents(components);

const FormCreatorComponent = ({ initialFormData, onFormSubmit, shouldEditForm, templateCode, userData, setShouldEditForm }) => {
    const navigate = useNavigate();
    const userJson = useReadMemory(MemoryType.USER)
    const [formData, setFormData] = useState(initialFormData);
    const [isLoading, setLoading] = useState(false);
    const [form] = TitleForm.useForm();
    const formService = new FormService();
    const configuration = useRecoilValue(currentConfiguration);
    const organizationIdentifier = useGetOrgIdentifier();
    const formBucketName = configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
    const [formStatus, setFormStatus] = useState([]);
    const [formStatusLoading, setFormStatusLoading] = useState(false);
    const [formVisibility, setFormVisibility] = useState([]);
    const [formVisibilityLoading, setFormVisibilityLoading] = useState(false);
    const [formCategories, setFormCategories] = useState([]);
    const [formCategoriesLoading, setFormCategoriesLoading] = useState(false);

    /*
     * This function created the json based on the form elements
     */
    const onFormDataSubmitted = async (values) => {
        setLoading(!isLoading)
        // Step 0: Construct Form Data
        const formDataObject = {};
        formDataObject["formName"] = values["form-name"];
        formDataObject["formDescription"] = values["description"];
        formDataObject["formType"] = values["form-type"];
        formDataObject["formStatus"] = values["form-status"];
        formDataObject["formCategory"] = values["form-category"];
        formDataObject["countTowards5percent"] = values["countTowards5percent"];
        formDataObject["formMetadata"] = JSON.stringify({
            attributes: values["form-metadata"]
        });
        setFormData(JSON.stringify(formDataObject));

        // // Step 1: Upload form and get the object ID
        var formDataBaseEncodedString = base64.encode(JSON.stringify(formData));
        const formUploadResult = await formService.uploadFormTemplate(formDataBaseEncodedString, formBucketName);
        // Step 2: Either edit the form with the object ID or create a new form using the object id by making an API call
        if (shouldEditForm) {
            await formService.updateForm(organizationIdentifier, '2tru9', values['form-name'], values['description'], values['form-type'], values['form-status'], values['form-category'], values.countTowards5percent, formDataObject["formMetadata"], formUploadResult.objectId, templateCode)
            publishEvent("niramaya.formbuilder.form.updated", userJson, formDataObject)
        } else {
            await formService.addFormTemplateRequest(organizationIdentifier, '2tru9', values['form-name'], values['description'], values['form-type'], values['form-status'], values['form-category'], values.countTowards5percent, formDataObject["formMetadata"], formUploadResult.objectId)
            publishEvent("niramaya.formbuilder.form.added", userJson, formDataObject)
        }

        // Resetting the form fields
        form.resetFields();

        // Calling the callback to trigger after the form creation is completed
        const formResult = {};
        formResult["formName"] = formDataObject["formName"];
        formResult["formDescription"] = formDataObject["formDescription"];
        formResult["formType"] = formDataObject["formType"];
        formResult["formStatus"] = formDataObject["formStatus"];
        formResult["formCategory"] = formDataObject["formCategory"];
        formResult["countTowards5percent"] = formDataObject["countTowards5percent"];
        formResult["formMetadata"] = formDataObject["formMetadata"];
        formResult["formContentId"] = formUploadResult.objectId;
        // onFormSubmit(false, formResult, true);
        onFormSubmit();
    };

    const onCancelButtonClick = () => {
        if (shouldEditForm) {
            setShouldEditForm(false);
        } else {
            navigate(-1);
        }
    };

    const handleFormChange = (schema) => {
        if (!isEqual(schema, formData)) {
            setFormData(schema);
        }
    }

    useEffect(() => {
        const getFormConfiguration = async () => {
            setFormStatusLoading(true);
            setFormVisibilityLoading(true);
            setFormCategoriesLoading(true);
            try {
                const payload = {
                    configurationKey: [
                        "niramaya.forms.status",
                        "niramaya.forms.membervisibility",
                        "niramaya.forms.categories"
                    ],
                };
                const response = await executeIntent(IntentType.GET_CONFIGURATION, payload,);

                if (response[0].configurationValue) {
                    const responseFormStatus = JSON.parse(response[0].configurationValue || "[]");
                    setFormStatus(responseFormStatus.map((item) => ({ label: item, value: item })));
                }

                if (response[1].configurationValue) {
                    const responseFormVisibility = JSON.parse(response[1].configurationValue || "[]");
                    const keys = responseFormVisibility.reduce((acc, obj) => [...acc, ...Object.keys(obj)], []);
                    setFormVisibility(keys.map((item) => ({ label: item, value: item })));
                }

                if (response[2].configurationValue) {
                    const responseFormCategories = JSON.parse(response[2].configurationValue || "[]");
                    setFormCategories(responseFormCategories.map((item) => ({ label: item?.activityType, value: item?.activityType })));
                }
            } catch (err) {
                notification.error({
                    message: "Error while getting form",
                    placement: "bottomRight",
                });
            } finally {
                setFormStatusLoading(false);
                setFormVisibilityLoading(false);
                setFormCategoriesLoading(false);
            }
        };

        getFormConfiguration();
    }, []);

    return (
        <div className="form">
            <div className="form-creator-back-button">
                {shouldEditForm ? 
                    <>
                        <ArrowBackIcon fontSize="large" onClick={onFormSubmit} className="back-button-style" />
                        <p>Edit form</p>
                    </>
                : <>
                    <BackButton />
                        <p>Create new form</p>
                    </>
                }
            </div>
            <TitleForm
                form={form}
                requiredMark={true}
                layout="vertical"
                name="edit_profile"
                onFinish={onFormDataSubmitted}
                className="submit_form"
                initialValues={{
                    "form-name": userData[0] || "",
                    "description": userData[1] || "",
                    "form-type": userData[2] || undefined,
                    "form-status": userData[3] || undefined,
                    "form-metadata": (userData[4] && JSON.parse(userData[4] ?? "{}")?.attributes) || [],
                    "form-category": userData[5] || undefined,
                    "countTowards5percent": userData[6] ?? undefined
                }}>
                <TitleForm.Item
                    name="form-name"
                    label="Form Name"
                    rules={[
                        {
                            required: true,
                            message: "Please input form name!",
                        },
                    ]}
                >
                    <Input maxLength={50} placeholder="Enter Form Name" disabled={false} />
                </TitleForm.Item>
                <TitleForm.Item
                    name="description"
                    label="Description"
                    rules={[
                        {
                            required: true,
                            message: "Please input Description!",
                        },
                    ]}
                >
                    <Input maxLength={100} placeholder="Enter Form Description" disabled={false} />
                </TitleForm.Item>
                <TitleForm.Item
                    name="countTowards5percent"
                    required={false}
                    label={
                        <>
                            Count Towards 5% ?<span style={{ color: '#FC797A' }}>&nbsp;*</span>
                            <Popover
                                overlayClassName="popover-helpertext"
                                placement="top"
                                content={"Hours reported with this form count towards the Conditions of Participation §418.70 requirement that at least 5% of the total clinical hours are contributed by volunteers."}
                                trigger={["click"]}
                            >
                                <span className="helpertext-icon">
                                    <InfoCircleFilled />
                                </span>
                            </Popover>
                        </>
                    }
                    rules={[
                        {
                            required: true,
                            message: "Please select an option!",
                        },
                    ]}
                >
                    <Radio.Group>
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                    </Radio.Group>
                </TitleForm.Item>
                <Row gutter={[20, 0]}>
                    <Col xs={24} md={8}>
                        <TitleForm.Item
                            name="form-status"
                            label="Status"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select Form Status!",
                                },
                            ]}
                        >
                            <Select placeholder="Select Form Status" options={formStatus} notFoundContent={formStatusLoading ? <Spin size="small" className="d-flex justify-content-center m-auto" /> : null} maxTagCount="responsive" getPopupContainer={(triggerNode) => triggerNode.parentElement}/>
                        </TitleForm.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <TitleForm.Item
                            name="form-metadata"
                            label="Attributes"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select Form Attributes!",
                                },
                            ]}
                        >
                            <Select placeholder="Select Form Access Attributes" options={formVisibility} mode="multiple" notFoundContent={formVisibilityLoading ? <Spin size="small" className="d-flex justify-content-center m-auto" /> : null} maxTagCount="responsive" getPopupContainer={(triggerNode) => triggerNode.parentElement}/>
                        </TitleForm.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <TitleForm.Item
                            name="form-category"
                            label="Categories"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select Form Categories!",
                                },
                            ]}
                        >
                            <Select placeholder="Select Form Categories" options={formCategories} notFoundContent={formCategoriesLoading ? <Spin size="small" className="d-flex justify-content-center m-auto" /> : null} maxTagCount="responsive" getPopupContainer={(triggerNode) => triggerNode.parentElement}/>
                        </TitleForm.Item>
                    </Col>
                </Row>
                <Space>
                    <Button loading={isLoading} shape="round" style={{ marginBottom: "26px" }} type="primary" htmlType="submit">
                        {shouldEditForm ? "Save" : "Create"}
                    </Button>
                    <Button onClick={onCancelButtonClick} shape="round" style={{ marginBottom: "26px" }} type="primary">
                        Cancel
                    </Button>
                </Space>
            </TitleForm>
            <FormBuilder
                form={formData}
                onChange={handleFormChange}
                onSubmit={(data) => { }}
                saveForm={(data) => setFormData(data)}
                saveText="Save Form"
                onSubmitDone={(data) => { }}
                options={{
                    builder: {
                        basic: {
                            components: {
                                selectPatientComponent: false
                            }
                        },
                    },
                }}
            />
        </div>
    );
};

export default FormCreatorComponent;
